import React from 'react';

type PreviewStatus = 'idle' | 'loading' | 'ready' | 'error';

interface GeneratePanelProps {
  canGeneratePreview: boolean;
  canGenerateVideo: boolean;
  isGeneratingPreview: boolean;
  isGeneratingVideo: boolean;
  previewStatus: PreviewStatus;
  previewError?: string | null;
  onGeneratePreview: () => void;
  onGenerateVideo: () => void;
}

const statusCopy: Record<PreviewStatus, { label: string; tone: 'muted' | 'info' | 'success' | 'error' }> = {
  idle: { label: 'Nog geen preview aangevraagd.', tone: 'muted' },
  loading: { label: 'Preview wordt gegenereerd…', tone: 'info' },
  ready: { label: 'Preview gereed. Gebruik deze voor de video.', tone: 'success' },
  error: { label: 'Preview genereren mislukt.', tone: 'error' },
};

const toneClasses: Record<'muted' | 'info' | 'success' | 'error', string> = {
  muted: 'text-[var(--color-text-secondary)]',
  info: 'text-[var(--color-primary)]',
  success: 'text-brand-green',
  error: 'text-brand-red',
};

export const GeneratePanel: React.FC<GeneratePanelProps> = ({
  canGeneratePreview,
  canGenerateVideo,
  isGeneratingPreview,
  isGeneratingVideo,
  previewStatus,
  previewError,
  onGeneratePreview,
  onGenerateVideo,
}) => {
  const status = statusCopy[previewStatus];
  const isPreviewReady = previewStatus === 'ready';
  const disableVideo = !canGenerateVideo || isGeneratingVideo || !isPreviewReady;

  return (
    <div className="card p-6 space-y-4">
      <div className="flex items-center gap-2">
        <svg className="w-5 h-5 text-[var(--color-primary)]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        <h2 className="text-lg font-semibold text-[var(--color-text-primary)]">Genereer</h2>
      </div>

      <p className="text-sm text-[var(--color-text-secondary)]">
        De preview-afbeelding wordt als referentie gebruikt bij het genereren van de video.
      </p>

      <div className="flex flex-col sm:flex-row gap-3">
        <button
          type="button"
          onClick={onGeneratePreview}
          disabled={!canGeneratePreview || isGeneratingPreview}
          className="btn-secondary flex-1 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isGeneratingPreview ? (
            <>
              <svg className="w-4 h-4 mr-2 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              Image preview...
            </>
          ) : (
            <>
              Generate
              <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </>
          )}
        </button>

        <button
          type="button"
          onClick={onGenerateVideo}
          disabled={disableVideo}
          className="btn-primary flex-1 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isGeneratingVideo ? (
            <>
              <svg className="w-4 h-4 mr-2 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              Video wordt gestart...
            </>
          ) : (
            <>
              Generate
              <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
              </svg>
            </>
          )}
        </button>
      </div>

      <div className={`text-xs ${toneClasses[status.tone]}`}>
        {status.label}
        {previewStatus === 'error' && previewError && (
          <span className="block text-[var(--brand-red)] mt-1">{previewError}</span>
        )}
        {!isPreviewReady && (
          <span className="block mt-1 text-[var(--color-text-secondary)]">
            Preview is aangeraden voordat je de video start.
          </span>
        )}
      </div>
    </div>
  );
};

export default GeneratePanel;
